<template>
  <div class="hike-item">
    <div class="hike-content d-flex">
      <!-- Thumbnail with play button -->
      <div class="hike-thumbnail-wrapper">
        <img :src="thumbnailUrl" class="hike-thumbnail" :alt="$t('hikes.hike_item.trail_thumbnail')" />

        <!-- Selection mode checkbox -->
        <div v-if="selectionMode" class="selection-checkbox-wrapper" @click.stop="emit('selection-changed', trail.id)">
          <div class="selection-checkbox" :class="{ selected: isSelected }">
            <i v-if="isSelected" class="material-icons">check</i>
          </div>
        </div>

        <button v-if="!selectionMode" class="play-button" @click="onShowFlyover" :disabled="isProcessing">
          <i class="material-icons">play_circle</i>
        </button>
      </div>

      <!-- Trail info -->
      <div class="hike-info">
        <!-- Badges row -->
        <div class="badges-row">
          <!-- Difficulty badge -->
          <span :class="['difficulty-badge', difficultyClass]">
            {{ difficultyLabel(trail.difficulty || 'Medium') }}
          </span>

          <!-- Private badge -->
          <span v-if="trail.publiclyVisible === false" class="private-badge" :title="$t('hikes.hike_item.private_hike')">
            <i class="material-icons">lock</i>
          </span>

          <!-- Processing badge -->
          <span v-if="isProcessing" class="processing-badge">
            <span class="processing-spinner"></span>
            {{ $t('hikes.hike_item.processing') }}
          </span>

          <!-- Hike ID badge (for tour providers only) -->
          <span
            v-if="props.isTourProvider && trail.id"
            class="hike-id-badge"
            @click="onCopyHikeId"
            :title="$t('hikes.hike_item.click_to_copy_hike_id')"
          >
            {{ $t('hikes.hike_item.hike_id_id', { id: trail.id }) }}
          </span>
        </div>

        <p class="mb-0">
          <a
            :href="isProcessing ? 'javascript:void(0)' : overviewUrl"
            class="hike-name-link two-lines"
            :class="{ 'disabled-link': isProcessing }"
          >
            <strong>{{ trail.name }}</strong>
          </a>
        </p>
        <p class="text-muted mb-0"><small style="font-size: .75em;">{{ formattedDate }} · {{ trail.location }}</small></p>
      </div>

      <!-- Stats -->
      <div class="hike-stats d-flex gap-3">
        <div class="stat">
          <span class="stat-label">{{ $t('shared.distance') }}</span>
          <p>{{ formattedDistance }}</p>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('hikes.hike_item.estimated_duration') }}</span>
          <p>{{ formattedDuration }}</p>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('shared.elevation') }}</span>
          <p>{{ formattedElevation }}</p>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="hike-actions d-flex align-items-center justify-content-start justify-content-md-center gap-2">
          <button class="btn btn-icon btn-yellow" @click="onDownload" :disabled="isProcessing">
            <span class="material-icons text-dark p-1 d-block" style="font-size: 28px;">arrow_circle_down</span>
          </button>
          <button class="btn btn-icon" @click="onAddToList" :disabled="isProcessing">
            <i class="material-icons">bookmark</i>
          </button>
          <div class="dropdown">
            <button class="btn btn-icon dropdown-toggle" type="button" data-bs-toggle="dropdown">
              <i class="material-icons">more_vert</i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" :class="{ disabled: isProcessing }" :href="isProcessing ? 'javascript:void(0)' : mapUrl">{{ $t('shared.view_map') }}</a></li>
              <li><button class="dropdown-item" :disabled="isProcessing" @click="onShowFlyover">{{ $t('shared.3d_flyover') }}</button></li>
              <li><a class="dropdown-item" :class="{ disabled: isProcessing }" :href="isProcessing ? 'javascript:void(0)' : printUrl">{{ $t('shared.print_map') }}</a></li>
              <li><button class="dropdown-item" :disabled="isProcessing" @click="onCopyShare">{{ $t('hikes.show.share_hike') }}</button></li>
              <li><button class="dropdown-item" :disabled="isProcessing" @click="onShareTrailer">{{ $t('hikes.show.hike_trailer') }}</button></li>
              <li><button class="dropdown-item" :disabled="isProcessing" @click="onExportGpx">{{ $t('hikes.hike_item.export_gpx') }}</button></li>
              <li v-if="!isPublicView"><button class="dropdown-item" :disabled="isProcessing" @click="onEditDetails">{{ $t('hikes.hike_item.edit_details') }}</button></li>
              <li v-if="!isPublicView"><button class="dropdown-item" :disabled="isProcessing" @click="onEditHike">{{ $t('hikes.hike_item.edit_hike') }}</button></li>
              <li v-if="!isPublicView"><button class="dropdown-item" :disabled="isProcessing" @click="onDuplicateHike">{{ $t('hikes.hike_item.duplicate_hike') }}</button></li>
              <li v-if="canSubmitOfficial"><button class="dropdown-item" :disabled="isProcessing" @click="onSubmitOfficial">{{ $t('hikes.show.submit_to_be_an_official_hike') }}</button></li>
              <li v-else-if="officialRequestPending"><button class="dropdown-item disabled text-muted" disabled>{{ $t('hikes.show.official_request_pending') }}</button></li>
              <li v-if="!isPublicView && props.isTrailsUser && trail.madeOfficialAt"><button class="dropdown-item text-warning" @click="onRemoveOfficial">{{ $t('hikes.show.remove_official_status') }}</button></li>
              <li v-if="!isPublicView"><button class="dropdown-item text-danger" @click="onDestroyHike">{{ $t('hikes.hike_item.delete_hike') }}</button></li>
            </ul>
          </div>
      </div>
    </div>
  </div>
  
</template>

<script setup>
import { computed } from 'vue'
import moment from 'moment'
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref'
import { HikeTimeUtility, PrettyTime } from '../../../utils/helpers/HikeTime'
import { qrCodeLinkForResource } from '../../../utils/helpers/DynamicLinkHelper'
import { difficultyLabel } from '../../../composables/useHikeFilters'
import { i18n } from '../../../i18n'

const t = (...a) => i18n.global.t(...a)

const props = defineProps({
  trail: { type: Object, required: true },
  isPublic: { type: Boolean, default: false },
  isTourProvider: { type: Boolean, default: false },
  isTrailsUser: { type: Boolean, default: false },
  selectionMode: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false }
})

const emit = defineEmits(['show-flyover', 'share-trailer', 'edit-hike', 'edit-details', 'export-gpx', 'copy-share', 'request-download', 'destroy-hike', 'duplicate-hike', 'add-to-list', 'selection-changed', 'remove-official', 'submit-official'])
const isPublicView = computed(() => !!props.isPublic)

const officialRequestPending = computed(() => !isPublicView.value && !props.trail?.madeOfficialAt && !!props.trail?.officialHikeApprovalRequestedAt)
const canSubmitOfficial = computed(() => !isPublicView.value && !!props.trail?.publiclyVisible && !props.trail?.madeOfficialAt && !props.trail?.officialHikeApprovalRequestedAt)

const overviewUrl = computed(() => props.trail?.shareableUrl)
const mapUrl = computed(() => `${props.trail?.shareableUrl}/map`)
const printUrl = computed(() => `/print-maps?data_ids[]=${props.trail?.id}&data_types[]=Hike`)

const thumbnailUrl = computed(() => {
  // Use static map image if available, otherwise use fallback
  if (props.trail?.staticMapImageUrl) {
    return props.trail.staticMapImageUrl
  }
  // Fallback to default hike thumbnail
  return new URL('../../../images/static_map_thumbnail.webp', import.meta.url).href
})

const formattedDate = computed(() => moment(props.trail?.createdAt).format('LLLL'))
const formattedDistance = computed(() => {
  const value = props.trail?.distanceMeters
  if (!value && value !== 0) return ''
  return MeasurementPrefUtility.preference().smallUnitFormatter(value, true)
})
const formattedElevation = computed(() => {
  const value = props.trail?.elevationGainMeters
  if (!value && value !== 0) return ''
  return MeasurementPrefUtility.preference().smallUnitFormatter(value, false)
})
const formattedDuration = computed(() => {
  const trail = props.trail
  if (trail?.trailType === 'multi_day') {
    const days = Array.isArray(trail?.hikeDays) ? trail.hikeDays.length : 0
    if (days <= 0) return '—'
    return days === 1 ? t('hikes.hike_item.one_day') : t('hikes.hike_item.count_days', { count: days })
  }
  const distance = trail?.distanceMeters
  const elevGain = trail?.elevationGainMeters
  if ((distance || distance === 0) && (elevGain || elevGain === 0)) {
    const secs = HikeTimeUtility.preference().secondsDuration(elevGain, distance)
    return new PrettyTime(secs).prettyTime()
  }
  if (trail?.durationDisplay) return trail.durationDisplay
  return ''
})

const difficultyClass = computed(() => {
  const difficulty = (props.trail?.difficulty || 'medium').toLowerCase()
  if (difficulty === 'easy') return 'difficulty-easy'
  if (difficulty === 'medium') return 'difficulty-medium'
  if (difficulty === 'difficult' || difficulty === 'hard') return 'difficulty-hard'
  if (difficulty === 'extra difficult') return 'difficulty-extra'
  return 'difficulty-medium'
})

const isProcessing = computed(() => {
  const status = props.trail?.processingStatus
  // Show processing badge if status is 'pending' or 'processing'
  return status === 'pending' || status === 'processing'
})

function onShowFlyover (e) { emit('show-flyover', e, props.trail) }
function onEditDetails () { if (isPublicView.value) return; emit('edit-details', props.trail) }
function onEditHike (e) { if (isPublicView.value) return; emit('edit-hike', props.trail) }
function onExportGpx (e) { emit('export-gpx', props.trail) }
function onCopyShare (e) { emit('copy-share', e, props.trail) }
function onShareTrailer (e) { emit('share-trailer', e, props.trail) }
function onDuplicateHike () { if (isPublicView.value) return; emit('duplicate-hike', props.trail) }
function onRemoveOfficial () { if (isPublicView.value) return; emit('remove-official', props.trail) }
function onSubmitOfficial () { if (isPublicView.value) return; emit('submit-official', props.trail) }
function onDestroyHike () { if (isPublicView.value) return; emit('destroy-hike', props.trail) }
function onAddToList () { emit('add-to-list', props.trail) }

async function onCopyHikeId () {
  if (!props.trail?.id) return
  try {
    await navigator.clipboard.writeText(String(props.trail.id))
    showToast(t('hikes.hike_item.hike_id_copied'))
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = String(props.trail.id)
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    document.body.appendChild(textArea)
    textArea.select()
    try {
      document.execCommand('copy')
      showToast(t('hikes.hike_item.hike_id_copied'))
    } catch (err) {
      showToast(t('hikes.hike_item.failed_to_copy_hike_id'))
    }
    document.body.removeChild(textArea)
  }
}

function showToast (message) {
  // Create toast element
  const toast = document.createElement('div')
  toast.className = 'hike-id-toast'
  toast.textContent = message
  document.body.appendChild(toast)

  // Trigger animation
  setTimeout(() => toast.classList.add('show'), 10)

  // Remove after animation
  setTimeout(() => {
    toast.classList.remove('show')
    setTimeout(() => document.body.removeChild(toast), 300)
  }, 2000)
}

async function onDownload () {
  emit('request-download', props.trail)
  try {
    const t = props.trail || {}
    if (t?.mainCountry?.slug && t?.mainRegion?.slug && t?.slug) {
      window.country_slug = t.mainCountry.slug
      window.region_slug = t.mainRegion.slug
      window.slug = t.slug
    }
    if (t?.id) {
      window.trail_id = t.id
      window.trailId = t.id
      window.hike_id = t.id
      window.id = t.id
    }
    if (t?.name) {
      window.trail_name = t.name
      window.trailName = t.name
    }
    if (t?.shareableUrl) {
      window.trail_share_url = t.shareableUrl
      try {
        const url = new URL(t.shareableUrl, window.location.origin)
        window.trail_download_path = url.pathname
      } catch (_) {
        window.trail_download_path = t.shareableUrl
      }
    }
    if (t?.id) {
      window.qrCodeUrl = await qrCodeLinkForResource({ type: 'Hike', id: t.id, fallbackUrl: t.shareableUrl })
    }
    if (window.openDownloadModal) {
      window.openDownloadModal()
    }
  } catch (e) {
    // no-op fallback; parent handler will attempt to open modal
  }
}

</script>

<style scoped>
.hike-item {
  background: #1f2026;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s;
}

.hike-item:hover {
  background: #252530;
  border-color: rgba(255, 255, 255, 0.12);
}

.hike-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  height: 100px;
}

.hike-thumbnail-wrapper {
  position: relative;
  width: 160px;
  height: -webkit-fill-available;
  background: var(--gray);
  flex: 0 0 15%;
  overflow: hidden;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

.hike-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-button {
  position: absolute;
  top: .5em;
  left: .5em;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}

.play-button:hover {
  background: rgba(0, 0, 0, 0.9);
}

.play-button i {
  font-size: 20px;
}

/* Hike Info */
.hike-info {
  flex: 0 0 23%;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.badges-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.difficulty-badge {
  display: inline-block;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.5px;
}

.difficulty-easy { background: #4BC263; color: var(--dark); }
.difficulty-medium { background: #F57700; color: var(--dark); }
.difficulty-hard { background: #DB2B35; color: #fff; }
.difficulty-extra { background: #8B008B; color: #fff; }

.private-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}

.private-badge i {
  font-size: 12px;
}

.processing-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.5px;
  background: rgba(251, 205, 1, 0.2);
  color: #FBCD01;
  border: 1px solid rgba(251, 205, 1, 0.4);
}

.processing-spinner {
  width: 8px;
  height: 8px;
  border: 2px solid rgba(251, 205, 1, 0.3);
  border-top-color: #FBCD01;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.hike-id-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.4);
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.hike-id-badge:hover {
  background: rgba(59, 130, 246, 0.3);
  border-color: rgba(59, 130, 246, 0.6);
  color: #93c5fd;
}

.hike-id-badge:active {
  transform: scale(0.95);
}

.hike-title { font-size: 20px; font-weight: 700; margin: 0; line-height: 1.2; }

.hike-name-link { color: #fff; text-decoration: none; transition: text-decoration 0.2s; }
.hike-name-link:hover { color: #fff; text-decoration: underline; }
.hike-name-link.disabled-link { opacity: 0.5; pointer-events: none; cursor: not-allowed; }

.hike-location { color: rgba(255, 255, 255, 0.5); font-size: 14px; margin: 0; line-height: 1.4; }

.hike-stats { display: flex; flex: 0 0 32%; gap: 3rem; margin-top: 0.75rem; }

.stat { display: flex; flex-direction: column; gap: 0.25rem; }
.stat-label { font-size: .5em; letter-spacing: 0.5px; color: #a6a6a6; }
.stat-value { color: #fff; font-size: 15px; font-weight: 600; }

/* Action Buttons */
.hike-actions { display: flex; flex: 1; align-items: center; padding-right: 1em; gap: 0.75rem; margin-left: auto; }
.btn-icon { background: transparent; border: 1px solid rgba(255, 255, 255, 0.15); color: rgba(255, 255, 255, 0.7); width: 36px; height: 36px !important; border-radius:100%; display: flex; align-items: center; justify-content: center; padding: 0; transition: all 0.2s; cursor: pointer; }
.btn-icon:hover { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.25); color: #fff; }
.btn-icon:disabled { opacity: 0.4; cursor: not-allowed; pointer-events: none; }
.btn-icon i { font-size: 20px; }
.btn-yellow { background: #FBCD01; color: #000; border: none; }
.btn-yellow:hover { background: #e6ba00; }
.btn-yellow:disabled { background: #9a8601; opacity: 0.5; }
.btn-yellow i { color: #000; }
.play-button:disabled { opacity: 0.4; cursor: not-allowed; }

/* Dropdowns */
.dropdown-toggle::after { display: none; }
.dropdown-menu { background: #252530; border: 1px solid rgba(255, 255, 255, 0.1); box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4); border-radius: 8px; padding: 0.5rem; margin-top: 0.5rem; }
.dropdown-item { color: rgba(255, 255, 255, 0.9); padding: 0.5rem 1rem; border-radius: 6px; font-size: 14px; }
.dropdown-item:hover,
.dropdown-item:focus-visible { background: rgba(255, 255, 255, 0.08); color: #fff; }
.dropdown-item:disabled,
.dropdown-item.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}
.dropdown-item:disabled:hover,
.dropdown-item:disabled:focus-visible,
.dropdown-item.disabled:hover,
.dropdown-item.disabled:focus-visible {
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
}

/* Selection Mode Checkbox */
.selection-checkbox-wrapper {
  position: absolute;
  top: 0.5em;
  left: 0.5em;
  cursor: pointer;
  z-index: 10;
}

.selection-checkbox {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.6);
  border: 2px solid rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}

.selection-checkbox:hover {
  background: rgba(0, 0, 0, 0.8);
  border-color: rgba(255, 255, 255, 0.8);
}

.selection-checkbox.selected {
  background: #FBCD01;
  border-color: #FBCD01;
}

.selection-checkbox i {
  font-size: 20px;
  color: #000;
}

/* ---------------- Responsive Tweaks ---------------- */
@media (max-width: 992px) {
  .hike-content {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .hike-thumbnail-wrapper {
    width: 100%;
    height: 180px;
    flex: none;
  }

  .hike-info {
    flex: none;
    padding: 0 .25rem;
  }

  .hike-stats {
    flex: none;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 0;
  }

  .hike-actions {
    flex: none;
    justify-content: flex-start;
    padding: 0 .25rem .5rem;
    margin-left: 0;
  }

  .btn-icon { width: 34px; height: 34px !important; }
}

@media (max-width: 576px) {
  .hike-thumbnail-wrapper { height: 140px; }
  .hike-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; }
  .btn-icon { width: 32px; height: 32px !important; }
}
</style>

<style>
/* Toast notification (global styles) */
.hike-id-toast {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%) translateY(100px);
  background: #1f2937;
  color: #fff;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  font-size: 14px;
  font-weight: 500;
  z-index: 10000;
  opacity: 0;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.hike-id-toast.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}
</style>
